import {
  ApprovalTerms,
  ApprovalBadge,
  ComparisonRowConfig,
  LenderApproval,
  GqlCreditSubmissionApproval,
  GqlLenderFee,
  LenderFeeType,
  Condition,
} from '../types';
import { formatCurrency } from '../../../utils';

type TermKey = keyof Omit<ApprovalTerms, 'conditions' | 'lenderFees'>;

/**
 * Determines which lenders get "best" and "worst" badges for a given row.
 * The `favorable` flag indicates whether the value is the best (true) or worst (false) for that metric.
 */
export function computeBadges(approvals: LenderApproval[], config: ComparisonRowConfig): Record<string, ApprovalBadge> {
  const result: Record<string, ApprovalBadge> = {};

  if (!config.bestIs || approvals.length < 2) return result;

  const key = config.key as TermKey;

  const numericValues = approvals
    .map(a => {
      const raw = a.approvalTerms[key];
      const value = typeof raw === 'number' ? raw : null;
      return { id: a.id, value };
    })
    .filter((entry): entry is { id: string; value: number } => entry.value !== null);

  if (numericValues.length < 2) return result;

  const sorted = [...numericValues].sort((a, b) => a.value - b.value);
  const minEntry = sorted[0];
  const maxEntry = sorted[sorted.length - 1];

  if (minEntry.value === maxEntry.value) return result;

  if (config.bestIs === 'highest') {
    result[maxEntry.id] = { label: 'highest', favorable: true };
    result[minEntry.id] = { label: 'lowest', favorable: false };
  } else {
    result[minEntry.id] = { label: 'lowest', favorable: true };
    result[maxEntry.id] = { label: 'highest', favorable: false };
  }

  return result;
}

/**
 * Computes badges specifically for the conditions count row.
 * Fewer conditions = favorable.
 */
export function computeConditionsBadges(approvals: LenderApproval[]): Record<string, ApprovalBadge> {
  const result: Record<string, ApprovalBadge> = {};

  if (approvals.length < 2) return result;

  const entries = approvals.map(a => ({
    id: a.id,
    count: a.approvalTerms.conditions.length,
  }));

  const sorted = [...entries].sort((a, b) => a.count - b.count);
  const minEntry = sorted[0];
  const maxEntry = sorted[sorted.length - 1];

  if (minEntry.count === maxEntry.count) return result;

  result[minEntry.id] = { label: 'fewest', favorable: true };
  result[maxEntry.id] = { label: 'most', favorable: false };

  return result;
}

// ---------------------------------------------------------------------------
// API → UI mapper
// ---------------------------------------------------------------------------

const APPROVED_LABELS = new Set(['Approved', 'Pre-Approved']);
// Raw Dynamics creditSubStage numeric codes that represent approvals
const APPROVED_STAGE_CODES = new Set(['804790003', '804790013']);

/**
 * Returns true when a credit submission represents an approval.
 * Checks both the human-readable decision field and the raw Dynamics stage code.
 */
export function isApproved(cs: { stage: string | null; decision: string | null }): boolean {
  return APPROVED_LABELS.has(cs.decision ?? '') || APPROVED_STAGE_CODES.has(cs.stage ?? '');
}

/**
 * Splits a contingencies string (plain text) into structured Condition objects.
 * Handles numbered lists, bullet points, semicolons, and plain newlines.
 */
export function parseContingencies(contingencies: string[] | null): Condition[] {
  if (!contingencies) return [];
  return contingencies
    .map(s => s.replace(/^\d+\.\s*/, '').trim())
    .filter(Boolean)
    .map((description, index) => ({ id: `cond-${index}`, description }));
}

/**
 * Maps a raw GqlCreditSubmissionApproval from the API to the internal LenderApproval shape.
 *
 * When AI-parsed data is available it takes precedence, otherwise the mapper
 * falls back to the traditional credit-submission fields and proposals.
 */
export function mapCreditSubmissionToApproval(cs: GqlCreditSubmissionApproval): LenderApproval {
  const parsed = cs.creditDecisionParsedData?.parsedData;

  // --- When we have AI-parsed data, prefer it --------------------------
  if (parsed) {
    const conditions: Condition[] =
      parsed.conditions && parsed.conditions.length > 0
        ? parsed.conditions
            .filter((d): d is string => d != null)
            .map((description, index) => ({ id: `cond-${index}`, description }))
        : [];

    return {
      id: cs.creditSubmissionId,
      lenderName: cs.lenderName ?? 'Unknown Lender',
      parsedDecisionId: cs.creditDecisionParsedData!.parsedDecisionId ?? null,
      approvalTerms: {
        conditions,
        interestRate: parsed.interestRate ?? null,
        approvedAmount: parsed.approvalAmount ?? null,
        monthlyPayment: parsed.monthlyPayment ?? null,
        termLengthMonths: parsed.termLengthMonths ?? null,
        daysToFirstPayment: parsed.daysToFirstPayment ?? null,
        minimumRequiredDownPayment: parsed.minimumRequiredDownPayment?.toString() ?? null,
        lenderFees: (parsed.lenderFees ?? []).filter((f): f is GqlLenderFee => f != null),
      },
    };
  }

  // --- Fallback: build from traditional CS fields + proposals -----------
  const conditions = parseContingencies(cs.contingencies);

  // Select proposal: prefer status === 'Accepted', fall back to first.
  const selectedProposal =
    cs.proposals.length > 0
      ? (cs.proposals.find(p => p?.status?.toLowerCase() === 'accepted') ?? cs.proposals[0])
      : undefined;

  const financeQuote = selectedProposal?.createdSnapshot?.settings?.configuration?.formData?.financeQuote;
  const pricing = selectedProposal?.createdSnapshot?.settings?.configuration?.pricing;

  return {
    parsedDecisionId: null,
    id: cs.creditSubmissionId,
    lenderName: cs.lenderName ?? 'Unknown Lender',
    approvalTerms: {
      conditions,
      approvedAmount: cs.approvedAmount ?? selectedProposal?.maxApprovedAmount ?? null,
      termLengthMonths: financeQuote?.terms != null ? financeQuote.terms?.join(', ') : null,
      daysToFirstPayment: financeQuote?.daysToFirstPayment ? financeQuote.daysToFirstPayment : null,
      interestRate: cs.approvedRate ?? selectedProposal?.interestRate ?? financeQuote?.commonRate ?? null,
      monthlyPayment: pricing
        ? Object.entries(pricing)
            .map(([term, price]: any) => `${term} mo: ${formatCurrency(price.paymentAmountPerPeriod, true)}`)
            .join(', ')
        : null,
      lenderFees: financeQuote?.fee
        ? financeQuote.fee.map((feeItem: any) => ({
            amount: feeItem.fee,
            description: feeItem.identifier ?? feeItem.type ?? null,
            feeName: feeItem.identifier,
            canBeFinanced: feeItem.financed,
            feeType: (feeItem.type as LenderFeeType) ?? null,
          }))
        : [],
      minimumRequiredDownPayment: financeQuote?.minimumRequiredDownPayment
        ? `${financeQuote.minimumRequiredDownPayment}%`
        : null,
    },
  };
}

/**
 * Formats a cell value based on its configured format type.
 */
export function formatCellValue(
  value: string | number | null | undefined,
  format: ComparisonRowConfig['format']
): string {
  if (value === null || value === undefined) return '—';

  switch (format) {
    case 'currency': {
      const num = Number(value);
      return isNaN(num) ? String(value) : `$${num.toLocaleString('en-US')}`;
    }

    case 'percentage':
      return `${value}%`;

    case 'months': {
      const num = Number(value);
      return isNaN(num) ? String(value) : `${num} mo`;
    }

    case 'number':
      return String(value);
    case 'text':

    default:
      return String(value);
  }
}
